import React from "react";
import { Link, useNavigate } from "react-router-dom";
import BeatLoader from "react-spinners/BeatLoader";
import { Box, TextField } from "@mui/material";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Checkbox from "@mui/material/Checkbox";
import { toast } from "react-toastify";
import { useCallbackPrompt } from "../../../../hooks/useCallbackPrompt";
import DialogBox from "../../../../components/DialogBox";
import { createNewDoseLevel } from "../../../../services/dose_levels";

import "../StudySetup.css";
import useDoseManagementDetails from "../../../../hooks/Api/useDoseManagement";
import { useDoseManagement } from "../../../../context/studyManagement/DoseManagement/DoseManagementContext";
import { buttonLinkStyle } from "../Visits/Questionnaires/EditVisitQuestionnaire";
import { handleBackNavigationGeneralFun } from "../../../../components/helpers/helper";

const CreateDoseLevels = () => {
  const navigate = useNavigate();

  const [load, setLoad] = React.useState(false);

  const [showDialog, setShowDialog] = React.useState(false);
  const [showPrompt, confirmNavigation, cancelNavigation] =
    useCallbackPrompt(showDialog);

  const { createDoseMutation, doseQuery } = useDoseManagementDetails();
  const { isLoading, data, refetch } = doseQuery;

  const createDosageSchema = yup.object().shape({
    name: yup.string().required("This field is required"),
    isRecommended: yup.bool().oneOf([true, false], "This field is required"),
    isEnabled: yup.bool().oneOf([true, false], "This field is required"),
    // canDispense: yup.bool().oneOf([true, false], "This field is required"),
  });

  const notify = () =>
    toast.success("Dose Level Created Successfully", {
      toastId: "form-creation",
    });

  const requestFailed = (msg) =>
    toast.error(`${msg}`, {
      toastId: "requestFailed",
    });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(createDosageSchema),
  });

  const onSubmit = async (data) => {
    const emptyFields = Object.keys(data).filter(
      (key) =>
        data[key] === undefined || data[key] === false || data[key] === ""
    ).length;

    if (emptyFields > 1) {
      toast.warning("Please fill all the fields", {
        toastId: "multiple-fields-error",
      });
      return;
    }
    try {
      setLoad(true);
      setShowDialog(false);
      const { name, isRecommended, isEnabled } = data;
      const newData = {
        name,
        isRecommended,
        isEnabled,
      };

      createDoseMutation.mutate(newData, {
        onSuccess: () => {
          notify();
          navigate("/study-management/dose-levels");
        },
        onError: (error) => {
          requestFailed(error.message);
        },
        onSettled: () => {
          setLoad(false);
        },
      });
    } catch (err) {
      setShowDialog(false);
      console.log("Error: ", err.message);
      requestFailed(err.message);
      setLoad(false); // Ensure loading state is reset
    }
  };

  const handleCancel = (e) => {
    e.preventDefault();
    if (isDirty) {
      const confirm = window?.confirm(
        "You have unsaved changes,Are you sure want to leave ?"
      );
      if (confirm) {
        navigate("/study-management/dose-levels");
      } else {
        e.preventDefault();
      }
    } else {
      navigate("/study-management/dose-levels");
    }
  };

  const loadContainerStyles = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "70vh",
  };

  const rowStyles = {
    marginTop: "2%",
    marginBottom: "2%",
  };

  const labelStyles = {
    display: "flex",
    alignItems: "center",
    height: "35px",
  };

  const textBoxBody = {
    marginLeft: "10px",
  };

  const textBoxStyles = {
    fontSize: 15,
    width: "400px",
    height: "10px",
    padding: "11px 14px",
  };

  const rowSpacing = {
    marginTop: "2%",
  };

  return (
    <>
      {isLoading ? (
        <>
          <div style={loadContainerStyles}>
            <BeatLoader color="#3661eb" />
          </div>
        </>
      ) : (
        <div className="content-body">
          <p className="study-management-link" style={{ fontWeight: "600" }}>
            <button
              style={buttonLinkStyle}
              onClick={(e) =>
                handleBackNavigationGeneralFun(
                  e,
                  showDialog,
                  "study-management",
                  navigate
                )
              }
            >
              Manage Your Study
            </button>{" "}
            |{" "}
            <button
              style={buttonLinkStyle}
              onClick={(e) =>
                handleBackNavigationGeneralFun(
                  e,
                  showDialog,
                  "study-management/dose-levels",
                  navigate
                )
              }
            >
              Dosage Level
            </button>{" "}
            | <Link to="/study-management/create-dose">Create Dosage</Link> -
            <b style={{ color: "gray" }}>
              {sessionStorage?.getItem("studyName")}
            </b>
          </p>
          <Row style={rowStyles}>
            <Col md={6}>
              <div className="study-management-head-start">
                <p className="study-management-heading">Create Dosage Level</p>
              </div>
            </Col>
            <Col md={6}></Col>
          </Row>

          <div>
            <DialogBox
              showDialog={showPrompt}
              confirmNavigation={confirmNavigation}
              cancelNavigation={cancelNavigation}
            />
          </div>

          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{ height: "auto", width: "100%" }}
            autoComplete="off"
          >
            <Row>
              <Col md={2}>
                <p className="descriptionLabel" style={labelStyles}>
                  Name
                  <span className="error-highlight">*</span>
                </p>
              </Col>
              <Col md={10}>
                <TextField
                  style={textBoxBody}
                  inputProps={{
                    style: textBoxStyles,
                  }}
                  {...register("name", {
                    onChange: (e) => {
                      setShowDialog(true);
                    },
                  })}
                />
                {errors.name && (
                  <>
                    <div className="createCaregiverFlexEnd">
                      <div className="createCaregiverError">
                        <span className="error-text">
                          {errors.name.message}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </Col>
            </Row>
            <Row style={rowSpacing}>
              <Col md={2}>
                <p className="descriptionLabel" style={labelStyles}>
                  Is Recommended{" "}
                  {errors.isRecommended?.message ? (
                    <span className="error-highlight">*</span>
                  ) : (
                    <></>
                  )}
                </p>
              </Col>
              <Col md={10}>
                <Checkbox
                  sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                  {...register("isRecommended", {
                    onChange: (e) => {
                      setShowDialog(true);
                    },
                  })}
                />
              </Col>
            </Row>

            {errors.isRecommended && (
              <Row>
                <Col md={2}></Col>
                <Col md={10}>
                  <p className="error-text">{errors.isRecommended.message}</p>
                </Col>
              </Row>
            )}

            <Row style={rowSpacing}>
              <Col md={2}>
                <p className="descriptionLabel" style={labelStyles}>
                  Is Enabled{" "}
                  {errors.isEnabled?.message ? (
                    <span className="error-highlight">*</span>
                  ) : (
                    <></>
                  )}
                </p>
              </Col>
              <Col md={10}>
                <Checkbox
                  sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                  {...register("isEnabled", {
                    onChange: (e) => {
                      setShowDialog(true);
                    },
                  })}
                />
              </Col>
            </Row>

            {errors.isEnabled && (
              <Row>
                <Col md={2}></Col>
                <Col md={10}>
                  <p className="error-text">{errors.isEnabled.message}</p>
                </Col>
              </Row>
            )}

            {/* <Row style={rowSpacing}>
              <Col md={2}>
                <p className="descriptionLabel" style={labelStyles}>
                  Can Dispense{" "}
                  {errors.canDispense?.message ? (
                    <span className="error-highlight">*</span>
                  ) : (
                    <></>
                  )}
                </p>
              </Col>
              <Col md={10}>
                <Checkbox
                  sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                  {...register("canDispense", {
                    onChange: (e) => {
                      setShowDialog(true);
                    },
                  })}
                />
              </Col>
            </Row>

            {errors.canDispense && (
              <Row>
                <Col md={2}></Col>
                <Col md={10}>
                  <p className="error-text">{errors.canDispense.message}</p>
                </Col>
              </Row>
            )} */}

            <Row style={rowSpacing}>
              <Col md={8}></Col>
              <Col md={2}>
                <div className="study-management-head-end">
                  {/* <Link to="/study-management-builder"> */}
                  <button
                    onClick={(e) => {
                      handleCancel(e);
                    }}
                    className="study-management-cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </Col>
              <Col md={2}>
                <div className="study-management-head-end">
                  {/* <Link to="/study-management-builder"> */}
                  <button
                    type="submit"
                    className="study-management-create-btn-md"
                  >
                    Save
                  </button>
                </div>
              </Col>
            </Row>
          </Box>
        </div>
      )}
    </>
  );
};

export default CreateDoseLevels;
